import { StoreThemeId, type PropertyPublic } from "@townsy/contracts";
import { STORE_THEME_META } from "@townsy/world";
import { errorMessage, isApiError } from "../api/client";

/**
 * Store interior themes (ADR-0006 #5, ADR-0008 #4): the 15 owner-facing presets, who may pick one,
 * their preview thumbnails, and what to say when the server refuses. Pure, so the rules are
 * unit-tested without a DOM.
 */
export interface StoreThemeOption {
  id: StoreThemeId;
  label: string;
  description: string;
}

export const STORE_THEME_OPTIONS: readonly StoreThemeOption[] = StoreThemeId.options.map((id) => ({
  id,
  label: STORE_THEME_META[id].label,
  description: STORE_THEME_META[id].description,
}));

/** Owner-facing name of a theme ("Bakery"), or null for non-stores / unknown ids. */
export function storeThemeLabel(theme: string | null | undefined): string | null {
  if (!theme) return null;
  const parsed = StoreThemeId.safeParse(theme);
  return parsed.success ? STORE_THEME_META[parsed.data].label : null;
}

/** The picker's explanation: presets are looks, not business categories, and changing is free. */
export const STORE_THEME_PICKER_INTRO =
  "Any preset fits any business: a bookshop can look like a lounge and a web studio like a pet shop. It's free, and you can change it again any time.";

// ---------------------------------------------------------------- preview thumbnails

/** Rendered previews of every preset (480 x 270 PNG, served from `public/store-themes/`). */
export const STORE_THEME_THUMB_WIDTH = 480;
export const STORE_THEME_THUMB_HEIGHT = 270;

export function storeThemeThumbnailUrl(id: StoreThemeId): string {
  return `/store-themes/${id}.png`;
}

/** Alt text of a preview (the label and description are next to it, so it names what is shown). */
export function storeThemeThumbnailAlt(option: Pick<StoreThemeOption, "label">): string {
  return `Preview of the ${option.label} interior`;
}

/**
 * Colours for the fallback when a preview image is missing or fails to load: the preset's wall
 * colour above its floor colour, so the choice still shows something of the look.
 */
const WALL_SWATCH: Record<string, string> = {
  cream: "#efe6d3",
  white: "#f7f7f4",
  sage: "#c9d6c1",
  sky: "#cfe2f3",
  blush: "#f2d4cf",
  sand: "#e6d5b8",
  slate: "#8c97a6",
  brick: "#b5654a",
  charcoal: "#3d4149",
};
const FLOOR_SWATCH: Record<string, string> = {
  wood_light: "#d8b98c",
  wood_dark: "#7a5434",
  tile_white: "#e9eaec",
  checker: "#2d2f36",
  concrete: "#a7a9ad",
  rubber: "#3b3d42",
  carpet: "#6c7a91",
  terrazzo: "#d9d3c8",
};

export function storeThemeSwatch(id: StoreThemeId): { wall: string; floor: string } {
  const meta = STORE_THEME_META[id];
  return { wall: WALL_SWATCH[meta.wall] ?? "#e3dccc", floor: FLOOR_SWATCH[meta.floor] ?? "#c9c0ab" };
}

// ---------------------------------------------------------------- who may pick

/**
 * The picker is offered only to a signed-in account whose OWN property read (made with its
 * session) lists `edit_interior` — the API adds it for the current commercial owner only. Room
 * broadcasts are public and never carry it, guests never get it, and the server re-checks on save.
 */
export function canPickStoreTheme(property: Pick<PropertyPublic, "type" | "permittedActions"> | null | undefined, signedIn: boolean): boolean {
  return Boolean(signedIn && property && property.type === "COMMERCIAL" && property.permittedActions.includes("edit_interior"));
}

export type ThemePickerAccessState = "ready" | "checking" | "sign_in" | "not_store" | "not_owner" | "error";

export interface ThemePickerAccess {
  state: ThemePickerAccessState;
  /** Why the picker cannot open (null when ready). */
  message: string | null;
}

/**
 * Whether the interior picker can open for a store, from the visitor's own property read. Right
 * after a purchase the cached read may predate it, so while a fresh read is in flight a missing
 * `edit_interior` means "checking", not "not yours".
 */
export function themePickerAccess(input: {
  property: Pick<PropertyPublic, "type" | "permittedActions"> | null | undefined;
  signedIn: boolean;
  fetching: boolean;
  error: unknown;
}): ThemePickerAccess {
  const { property, signedIn, fetching, error } = input;
  if (!signedIn) return { state: "sign_in", message: "Sign in with the account that owns this store to choose its interior." };
  if (canPickStoreTheme(property, signedIn)) return { state: "ready", message: null };
  if (fetching || (!property && !error)) return { state: "checking", message: "Checking that you own this store…" };
  if (!property) return { state: "error", message: `Couldn't load this store: ${errorMessage(error)}` };
  if (property.type !== "COMMERCIAL") return { state: "not_store", message: "Only stores have interior presets." };
  return {
    state: "not_owner",
    message: "Only the store's current owner can choose its interior. If you just bought it, the purchase may still be completing; try again in a moment.",
  };
}

/** Plain-language reason for a refused `PUT /properties/:id/interior-theme`. */
export function themeSaveErrorMessage(err: unknown): string {
  if (isApiError(err)) {
    if (err.status === 401 || err.code === "UNAUTHENTICATED") return "Sign in again to change your store's interior.";
    if (err.status === 403 || err.code === "FORBIDDEN") return "Only the store's current owner can change its interior. Ownership may have changed.";
    if (err.code === "PROPERTY_TYPE_NOT_ELIGIBLE") return "Only stores have interior themes.";
    if (err.status === 422 || err.code === "VALIDATION_FAILED") return "That theme isn't available. Pick one from the list.";
    if (err.status === 429 || err.code === "RATE_LIMITED") return "Too many changes in a short time. Wait a minute, then try again.";
    if (err.status === 404 || err.code === "NOT_FOUND") return "This store could not be found any more.";
  }
  return errorMessage(err, "The interior could not be changed. Try again.");
}
